import { useState, useEffect, useCallback } from 'react';
import { User } from '../../user/types/type';
import { userApi } from '../../services/api/user/userApi';
import dataProvider from '../../services/api';

interface UseContractorsProps {
  handleErrorWithLogout: (error: any) => void;
}

export const useContractors = ({ handleErrorWithLogout }: UseContractorsProps) => {
  const [contractors, setContractors] = useState<User[]>([]);
  const [selectedContractor, setSelectedContractor] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalContractors, setTotalContractors] = useState<number>(0);
  const [contractorsPerPage, setContractorsPerPage] = useState<number>(8); 

  const handleContractorsPerPageChange = useCallback((newContractorsPerPage: number) => {
    if (newContractorsPerPage !== contractorsPerPage) {
      setContractorsPerPage(newContractorsPerPage);
      setCurrentPage(1);
    }
  }, [contractorsPerPage]);

  const parseSearchTerm = (term: string) => {
    const cleanTerm = term.trim().toLowerCase();
    const words = cleanTerm.split(/\s+/).filter(word => word.length > 0);
    
    return {
      originalTerm: cleanTerm,
      words: words,
      firstName: words[0] || '',
      lastName: words[words.length - 1] || '',
      middleName: words.length > 2 ? words.slice(1, -1).join(' ') : ''
    };
  };

  const searchWithKeyword = async (keyword: string) => {
    try {
      const { data } = await userApi.getListUsers({
        filter: keyword,
        page: 0,
        perPage: 100, 
      });
      return data;
    } catch (error) {
      handleErrorWithLogout(error);
      return [];
    }
  };

  const isContractorMatchSearchTerm = (contractor: User, searchTerm: string) => {
    const fullName = `${contractor.firstName} ${contractor.middleName || ''} ${contractor.lastName}`.trim().toLowerCase();
    const searchLower = searchTerm.toLowerCase();

    if (fullName.includes(searchLower)) return true;

    const searchWords = searchLower.split(/\s+/);
    const nameWords = fullName.split(/\s+/);

    return searchWords.every(searchWord => 
      nameWords.some(nameWord => 
        nameWord.includes(searchWord) || searchWord.includes(nameWord)
      )
    );
  };

  const calculateRelevanceScore = (contractor: User, searchTerm: string) => {
    const fullName = `${contractor.firstName} ${contractor.middleName || ''} ${contractor.lastName}`.trim().toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    
    let score = 0;
    
    if (fullName === searchLower) score += 100;
    
    if (fullName.includes(searchLower)) score += 50;
    
    const searchWords = searchLower.split(/\s+/);
    const nameWords = fullName.split(/\s+/);
    
    searchWords.forEach(searchWord => {
      nameWords.forEach(nameWord => {
        if (nameWord === searchWord) score += 30;
        else if (nameWord.includes(searchWord)) score += 20;
        else if (searchWord.includes(nameWord)) score += 10;
      });
    });
    
    return score;
  };

  const smartSearch = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      const { data: totalData } = await dataProvider.userTotalCount({
        filter: '',
        count: contractorsPerPage,
      });

      const { data } = await userApi.getListUsers({
        filter: '',
        page: currentPage - 1,
        perPage: contractorsPerPage,
      });

      return { contractors: data, total: totalData.count };
    }

    const parsed = parseSearchTerm(searchTerm);
    let allContractors: User[] = [];
    
    const searchPromises = [];
    
    if (parsed.firstName) {
      searchPromises.push(searchWithKeyword(parsed.firstName));
    }
    
    if (parsed.lastName && parsed.lastName !== parsed.firstName) {
      searchPromises.push(searchWithKeyword(parsed.lastName));
    }
    
    if (parsed.middleName) {
      searchPromises.push(searchWithKeyword(parsed.middleName));
    }
    
    searchPromises.push(searchWithKeyword(searchTerm));

    const results = await Promise.all(searchPromises);
    
    results.forEach(result => {
      allContractors = allContractors.concat(result);
    });

    const uniqueContractors = allContractors.filter((contractor, index, self) => 
      index === self.findIndex(c => c.userId === contractor.userId)
    );

    const matchedContractors = uniqueContractors
      .filter(contractor => isContractorMatchSearchTerm(contractor, searchTerm))
      .map(contractor => ({
        ...contractor,
        relevanceScore: calculateRelevanceScore(contractor, searchTerm)
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);

    const startIndex = (currentPage - 1) * contractorsPerPage;
    const endIndex = startIndex + contractorsPerPage;
    const paginatedContractors = matchedContractors.slice(startIndex, endIndex);

    return {
      contractors: paginatedContractors,
      total: matchedContractors.length
    };
  };

  const fetchContractors = async () => {
    try {
      const result = await smartSearch(searchTerm);

      const mappedContractors = result.contractors.map((contractor: User) => ({
        ...contractor,
        name: `${contractor.firstName} ${contractor.middleName || ''} ${contractor.lastName}`.trim(),
        middleName: contractor.middleName || '',
        profileImageUrl: contractor.profileImageUrl ? 
          `${contractor.profileImageUrl}?t=${new Date().getTime()}` : '',
      }));

      setContractors(mappedContractors);
      setTotalContractors(result.total);

      if (mappedContractors.length > 0 && !selectedContractor) {
        setSelectedContractor(mappedContractors[0]);
      } else if (selectedContractor) {
        const updated = mappedContractors.find((c: User) => c.userId === selectedContractor.userId);
        if (updated) setSelectedContractor(updated);
      }
    } catch (error: any) {
      handleErrorWithLogout(error);
      setContractors([]);
      setTotalContractors(0);
      setSelectedContractor(null);
    }
  };

  useEffect(() => {
    fetchContractors();
  }, [currentPage, searchTerm, contractorsPerPage]); 

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleContractorSelect = (contractor: User) => {
    setSelectedContractor(contractor);
  };

  const handlePageChange = (page: number) => {
    const maxPage = Math.ceil(totalContractors / contractorsPerPage);
    setCurrentPage(Math.max(1, Math.min(page, maxPage)));
  };

  return {
    contractors,
    selectedContractor,
    searchTerm,
    currentPage,
    totalContractors,
    contractorsPerPage,
    setSearchTerm,
    handleContractorSelect,
    handlePageChange,
    refetchContractors: fetchContractors,
    onContractorsPerPageChange: handleContractorsPerPageChange, // Export callback
  };
};